'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { DESKTOP_NAV_LINKS, type MarketingNavLink } from '../config/marketing-nav';
import { MarketingNavItem } from './marketing-nav-item';
import { MarketingNavPopover } from './marketing-nav-popover';

// Width per popover body: the designed panels (Product's card-in-card 3x2
// grid, the Use cases / Resources column panels) need more room than the
// icon-row list. Every column is the same width across the three designed
// popovers: three-column panels share THREE_COL_WIDTH and two-column ones
// get exactly 2/3 of it.
const THREE_COL_WIDTH = 640;
const popoverWidthFor = (link: MarketingNavLink): number => {
	if (link.kind === 'product') return THREE_COL_WIDTH;
	if (link.kind === 'columns') {
		const columns =
			link.panelColumns ?? new Set((link.items ?? []).map((item) => item.group)).size;
		return columns >= 3 ? THREE_COL_WIDTH : Math.round((THREE_COL_WIDTH * 2) / 3);
	}
	return 320;
};

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];
const CLOSE_DELAY = 120;

interface PopoverState {
	/** Index into DESKTOP_NAV_LINKS of the open item. */
	index: number;
	/** Trigger centre, in px, relative to the nav row's left edge. */
	center: number;
	/** The nav row's left edge in viewport px - lets wide panels clamp to the viewport. */
	navLeft: number;
}

/**
 * MarketingNavLinks - the primary header nav. Each entry is a MarketingNavItem
 * trigger; the items with sub-links share ONE popover that travels horizontally
 * (spring on `x`) to sit under the hovered trigger while its content swaps and
 * its height morphs - so moving between e.g. Product → Resources reads as a
 * single panel sliding, not two popovers fading past each other.
 *
 * Content swaps crossfade: sync AnimatePresence keeps the outgoing panel
 * fading out while the incoming one fades in on top (both layers are
 * absolutely positioned inside the clipped card), so there is never an empty
 * frame between menus, and the incoming layer's mount measurement drives the
 * height spring. Robust close paths: Escape and pointer-down
 * outside close from anywhere on the page, and clicking any link inside the
 * panel closes it immediately.
 *
 * Hidden below md; MarketingMobileNav owns small screens.
 */
export const MarketingNavLinks = () => {
	const navRef = useRef<HTMLDivElement>(null);
	const resizeObserver = useRef<ResizeObserver | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [popover, setPopover] = useState<PopoverState | null>(null);
	const [height, setHeight] = useState<number>();

	const cancelClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);

	// Clearing height too means a reopened menu paints at its own height instead
	// of springing from the previously-open menu's height during the fade-in.
	const close = useCallback(() => {
		setPopover(null);
		setHeight(undefined);
	}, []);

	const scheduleClose = useCallback(() => {
		cancelClose();
		closeTimer.current = setTimeout(close, CLOSE_DELAY);
	}, [cancelClose, close]);

	const handleTriggerEnter = useCallback(
		(index: number) => (trigger: HTMLElement) => {
			cancelClose();
			// Plain links (no sub-items) dismiss whatever popover is open.
			if (!DESKTOP_NAV_LINKS[index].items?.length) {
				close();
				return;
			}
			const nav = navRef.current;
			if (!nav) return;
			const navRect = nav.getBoundingClientRect();
			const triggerRect = trigger.getBoundingClientRect();
			setPopover({
				index,
				center: triggerRect.left - navRect.left + triggerRect.width / 2,
				navLeft: navRect.left,
			});
		},
		[cancelClose, close],
	);

	// Measure the active content the moment it mounts (callback ref, before
	// paint) so the card height is always defined for the content on screen -
	// never a half-drawn sliver waiting on an effect - and keep a
	// ResizeObserver on it so in-content reflow (e.g. fonts) re-springs the
	// height.
	const attachContent = useCallback((node: HTMLDivElement | null) => {
		// During a crossfade the outgoing layer still carries this ref and
		// detaches (null) AFTER the incoming layer attached - ignoring null keeps
		// the incoming layer's observer alive; attaching always replaces it.
		if (!node) return;
		resizeObserver.current?.disconnect();
		setHeight(node.offsetHeight);
		const observer = new ResizeObserver(() => setHeight(node.offsetHeight));
		observer.observe(node);
		resizeObserver.current = observer;
	}, []);

	// Escape and pointer-down outside close from anywhere on the page - the
	// popover can never be left stuck open over content.
	const isOpen = popover != null;
	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') close();
		};
		const onPointerDown = (event: PointerEvent) => {
			const nav = navRef.current;
			if (nav && event.target instanceof Node && !nav.contains(event.target)) close();
		};
		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('pointerdown', onPointerDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('pointerdown', onPointerDown);
		};
	}, [isOpen, close]);

	const activeLink = popover != null ? DESKTOP_NAV_LINKS[popover.index] : null;
	const popoverWidth = activeLink ? popoverWidthFor(activeLink) : 320;
	// Centre under the trigger, then clamp inside the viewport (16px margins) -
	// the wide Product/Resources panels would otherwise clip off the left edge
	// under the first triggers. `x` is relative to the nav row's left edge.
	const targetX = (() => {
		if (!popover) return 0;
		const centered = popover.center - popoverWidth / 2;
		const minX = 16 - popover.navLeft;
		const maxX =
			(typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth - 16) -
			popover.navLeft -
			popoverWidth;
		return Math.min(Math.max(centered, minX), Math.max(maxX, minX));
	})();

	return (
		<div
			ref={navRef}
			className="relative hidden items-center gap-1 md:flex"
			onMouseLeave={scheduleClose}
		>
			{DESKTOP_NAV_LINKS.map((link, index) => (
				<MarketingNavItem
					key={link.label}
					link={link}
					isOpen={popover?.index === index}
					onTriggerEnter={handleTriggerEnter(index)}
					onTriggerLeave={scheduleClose}
				/>
			))}

			<AnimatePresence>
				{activeLink && popover && (
					<motion.div
						key="nav-popover"
						initial={{ opacity: 0, y: -6, x: targetX }}
						animate={{ opacity: 1, y: 0, x: targetX }}
						exit={{ opacity: 0, y: -6 }}
						transition={{
							default: { duration: 0.18, ease: EASE },
							x: { type: 'spring', stiffness: 420, damping: 36 },
						}}
						style={{ transformOrigin: 'top center' }}
						className="absolute left-0 top-full z-50 pt-3"
						role="menu"
						onMouseEnter={cancelClose}
						onMouseLeave={scheduleClose}
						onClick={(event) => {
							// A chosen link should dismiss the panel immediately - client-side
							// navigation keeps the header mounted, so hover state alone would
							// leave the popover hanging over the next page.
							if ((event.target as HTMLElement).closest('a')) close();
						}}
					>
						<motion.div
							animate={{ height: height ?? undefined, width: popoverWidth }}
							transition={{
								height: { type: 'spring', stiffness: 500, damping: 42 },
								width: { type: 'spring', stiffness: 500, damping: 42 },
							}}
							initial={false}
							style={{ width: popoverWidth }}
							// Page-black background (the marketing canvas token) - the panel
							// matches the page instead of floating a lighter translucent card;
							// the inner card reads through hairlines alone. No backdrop blur.
							className="relative overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
						>
							{/* Sync mode: the incoming and outgoing layers overlap so menus
							    crossfade with no empty frame; the incoming layer's mount
							    measurement drives the height spring. */}
							<AnimatePresence initial={false}>
								<motion.div
									key={popover.index}
									ref={attachContent}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.45, ease: EASE }}
									// Viewport cap: long menus scroll inside the card instead of
									// growing past a small laptop screen. The height measurement
									// reads this element, so the card springs to the capped
									// height automatically.
									className="absolute inset-x-0 top-0 max-h-[calc(100dvh-140px)] w-full overflow-y-auto overscroll-contain"
								>
									<MarketingNavPopover link={activeLink} />
								</motion.div>
							</AnimatePresence>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
